import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DateRangeCalendar from './DateRangeCalendar';
import useDateRangeState, {
  buildPresetRanges,
  diffDaysInclusive,
  formatDisplay,
  toDateOnly
} from './useDateRangeState';
import './date-range.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function buildTriggerLabel(from, to) {
  if (from && to) {
    return `${formatDisplay(from)} — ${formatDisplay(to)}`;
  }
  if (from) {
    return `${formatDisplay(from)} — Choose end date`;
  }
  if (to) {
    return `Choose start date — ${formatDisplay(to)}`;
  }
  return 'Select start and end dates';
}

function buildLiveRegionMessage(from, to) {
  if (!from && !to) return '';
  if (from && to) {
    const isoFrom = toDateOnly(from);
    const isoTo = toDateOnly(to);
    if (isoFrom === isoTo) {
      return `Start ${isoFrom} selected (single day)`;
    }
    const days = diffDaysInclusive(from, to);
    return `Start ${isoFrom} and end ${isoTo} selected (${days} day${days === 1 ? '' : 's'})`;
  }
  if (from) {
    return `Start ${toDateOnly(from)} selected, pick an end date or submit as a single day`;
  }
  return `End ${toDateOnly(to)} selected, pick a start date`;
}

function DateRangeInput({
  id,
  fromDate,
  toDate,
  onChange,
  disabled = false,
  ariaInvalid = false
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const panelId = `${id}-panel`;
  const liveRegionId = `${id}-live`;

  const { selected, setRange, defaultMonth } = useDateRangeState({
    fromDate,
    toDate,
    onChange
  });

  const presets = useMemo(() => buildPresetRanges(), []);

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    function handleDocumentMouseDown(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeAndRestoreFocus();
        return;
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeAndRestoreFocus]);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return;
    const container = popoverRef.current;
    if (!container) return;
    const firstFocusable = container.querySelector(FOCUSABLE_SELECTOR);
    if (firstFocusable) firstFocusable.focus();
  }, [open]);

  const triggerLabel = buildTriggerLabel(selected.from, selected.to);
  const liveMessage = buildLiveRegionMessage(selected.from, selected.to);

  const handleSelect = useCallback(
    (range) => {
      setRange(range);
    },
    [setRange]
  );

  const handlePresetClick = useCallback(
    (preset) => {
      setRange({ from: preset.from, to: preset.to });
    },
    [setRange]
  );

  return (
    <div className="widget-date-range dr-root" ref={rootRef}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`widget-form-input dr-trigger${
          ariaInvalid ? ' has-error' : ''
        }`}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? panelId : undefined}
        aria-describedby={liveRegionId}
        data-invalid={ariaInvalid ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span className="dr-trigger-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
            <rect
              x="2"
              y="3"
              width="12"
              height="11"
              rx="2"
              ry="2"
              stroke="currentColor"
              strokeWidth="1.3"
              fill="none"
            />
            <path
              d="M2 6 H14 M5 2 V4 M11 2 V4"
              stroke="currentColor"
              strokeWidth="1.3"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className="dr-trigger-value">
          {triggerLabel}
        </span>
        <span className="dr-trigger-caret" aria-hidden="true">
          {open ? '\u25B4' : '\u25BE'}
        </span>
      </button>

      <span
        id={liveRegionId}
        className="dr-sr-only"
        role="status"
        aria-live="polite"
      >
        {liveMessage}
      </span>

      {open && !disabled && (
        <div
          id={panelId}
          ref={popoverRef}
          className="dr-popover"
          role="group"
          aria-label="Date range calendar"
        >
          <div className="dr-popover-header">
            <p className="dr-popover-heading">
              Choose a start and end date
            </p>
            <button
              type="button"
              className="dr-popover-close"
              aria-label="Close date range calendar"
              disabled={disabled}
              onClick={closeAndRestoreFocus}
            >
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                <path
                  d="M4 4 L12 12 M12 4 L4 12"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div
            className="dr-preset-row"
            role="group"
            aria-label="Preset date ranges"
          >
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className="dr-preset-pill"
                disabled={disabled}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <DateRangeCalendar
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={defaultMonth}
          />

          <div className="dr-popover-footer">
            <button
              type="button"
              className="dr-popover-done"
              disabled={disabled}
              onClick={closeAndRestoreFocus}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangeInput;
